<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-project-diagram"></i> 流程名称</label>
          <input v-model="searchForm.processName" type="text" placeholder="请输入流程名称" class="search-input" list="draftProcessList" @focus="onProcessNameFocus" autocomplete="off">
          <datalist id="draftProcessList">
            <option v-for="name in processNameOptions" :key="name" :value="name">{{ name }}</option>
          </datalist>
        </div>
        <div class="search-item">
          <label><i class="fas fa-align-left"></i> 摘要信息</label>
          <input v-model="searchForm.summary" type="text" placeholder="请输入摘要信息" class="search-input" style="width:200px" autocomplete="off">
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary search-btn">
            <i class="fas fa-search"></i> 查询
          </button>
          <button @click="resetSearch" class="btn btn-default search-btn">
            <i class="fas fa-undo"></i> 重置
          </button>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <div class="table-header-left"></div>
        <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn">
          <i class="fas fa-columns"></i> 列设置
        </button>
      </div>
      
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th 
                v-for="column in visibleColumns" 
                :key="column.key"
                :class="{ 'sortable': column.sortable, 'sorted-asc': sortColumn === column.key && sortOrder === 'asc', 'sorted-desc': sortColumn === column.key && sortOrder === 'desc' }"
                @click="handleSort(column.key)"
                :draggable="true"
                @dragstart="dragStart($event, column)"
                @dragover.prevent
                @drop="dropColumn($event, column)"
                class="column-header"
                :style="column.width ? { width: column.width, maxWidth: column.width, minWidth: column.width } : {}"
              >
                <span>{{ column.label }}</span>
                <i v-if="column.sortable" class="fas" :class="sortColumn === column.key ? (sortOrder === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down') : 'fa-sort'"></i>
              </th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="draft in paginatedDrafts" :key="draft.id" @dblclick="editDraft(draft)" class="task-row">
              <td v-for="column in visibleColumns" :key="column.key" :style="column.width ? { width: column.width, maxWidth: column.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}">
                {{ draft[column.key] }}
              </td>
              <td class="action-column">
                <button @click="editDraft(draft)" class="btn btn-primary btn-xs" title="编辑">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="deleteDraft(draft)" class="btn btn-danger btn-xs" title="删除">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="paginatedDrafts.length === 0">
              <td :colspan="visibleColumns.length + 1" class="empty-cell">暂无草稿</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">
          共 {{ totalDrafts }} 条记录
        </div>
        <div class="pagination-controls">
          <div class="page-size-selector">
            <span>每页</span>
            <select v-model="pageSize" class="page-size-input form-control">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
            <span>条</span>
          </div>
          <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              @click="goToPage(page)"
              :class="['page-btn', { active: currentPage === page, disabled: page === '...' }]"
              :disabled="page === '...'"
            >
              {{ page }}
            </button>
          </div>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="btn btn-default btn-sm pagination-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
          <div class="page-jump">
            <span>跳转到</span>
            <input 
              type="number" 
              v-model="jumpPage" 
              min="1" 
              :max="totalPages"
              class="jump-input"
              @keyup.enter="handleJump"
            >
            <span>页</span>
            <button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showColumnSelector" class="column-selector-modal" @click.self="toggleColumnSelector">
      <div class="column-selector-content">
        <div class="column-selector-header">
          <h4>列设置</h4>
          <button @click="toggleColumnSelector" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="column-selector-body">
          <label v-for="column in allColumns" :key="column.key" class="column-checkbox">
            <input type="checkbox" v-model="column.visible" @change="updateVisibleColumns">
            <span>{{ column.label }}</span>
          </label>
        </div>
        <div class="column-selector-footer">
          <button @click="resetColumns" class="btn btn-default">重置</button>
          <button @click="toggleColumnSelector" class="btn btn-primary">确定</button>
        </div>
      </div>
    </div>

    <!-- 草稿编辑弹窗（复用任务发起弹窗） -->
    <TaskLaunchModal
      :visible="draftModalVisible"
      :process-id="editingDraft?.processGuid || ''"
      :process-name="editingDraft?.processName || ''"
      :draft-guid="editingDraft?.guid || ''"
      @close="handleDraftClose"
      @success="handleDraftSuccess"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getDrafts, deleteDrafts as deleteDraftsApi, buildPagedRequest } from '../api/task'
import TaskLaunchModal from '../components/TaskLaunchModal.vue'

const showColumnSelector = ref(false)
const currentPage = ref(1)
const pageSize = ref(20)
const sortColumn = ref('processName')
const sortOrder = ref('asc')
const jumpPage = ref(1)

const searchForm = reactive({
  processName: '',
  summary: ''
})

const allColumns = ref([
  { key: 'processName', label: '流程名称', sortable: true, visible: true, width: '200px' },
  { key: 'catalogName', label: '流程目录', sortable: true, visible: true, width: '140px' },
  { key: 'createTime', label: '创建时间', sortable: true, visible: true, width: '160px' },
  { key: 'summary', label: '摘要信息', sortable: false, visible: true, width: '280px' }
])

const drafts = ref([])

const fmt = (v) => {
  if (v === undefined || v === null) return ''
  return String(v).replace('T', ' ').substring(0, 19)
}
const mapDraft = (d) => ({
  id: d.guid || d.Guid,
  guid: d.guid || d.Guid,
  processGuid: d.processGuid || d.ProcessGuid,
  processName: d.processName || d.ProcessName || '',
  catalogName: d.catalogName || d.CatalogName || '',
  createTime: fmt(d.createAt || d.CreateAt),
  summary: d.description || d.Description || d.summaryInfo || d.SummaryInfo || ''
})

const loadDrafts = async () => {
  try {
    const res = await getDrafts(buildPagedRequest({ pageNumber: 0, pageSize: 500, searchText: searchForm.processName || '' }))
    drafts.value = (res.rows || res.Rows || []).map(mapDraft)
  } catch (e) {
    console.error('加载草稿箱失败', e)
  }
}

onMounted(loadDrafts)

const visibleColumns = computed(() => allColumns.value.filter(col => col.visible))
const processNameOptions = computed(() => [...new Set(drafts.value.map(d => d.processName).filter(Boolean))].sort())
const onProcessNameFocus = () => {}

const filteredDrafts = computed(() => {
  let result = [...drafts.value]
  
  if (searchForm.processName) {
    result = result.filter(d => d.processName.toLowerCase().includes(searchForm.processName.toLowerCase()))
  }
  if (searchForm.summary) {
    result = result.filter(d => d.summary.toLowerCase().includes(searchForm.summary.toLowerCase()))
  }
  
  if (sortColumn.value && sortColumn.value !== 'summary') {
    result.sort((a, b) => {
      const aVal = a[sortColumn.value]
      const bVal = b[sortColumn.value]
      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
      return 0
    })
  }
  
  return result
})

const totalDrafts = computed(() => filteredDrafts.value.length)
const totalPages = computed(() => Math.ceil(totalDrafts.value / pageSize.value) || 1)

const paginatedDrafts = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filteredDrafts.value.slice(start, start + pageSize.value)
})

const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = currentPage.value
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else {
    pages.push(1)
    if (current > 4) pages.push('...')
    const start = Math.max(2, current - 2)
    const end = Math.min(total - 1, current + 2)
    for (let i = start; i <= end; i++) pages.push(i)
    if (current < total - 3) pages.push('...')
    pages.push(total)
  }
  return pages
})

const handleSearch = () => {
  currentPage.value = 1
  loadDrafts()
}

const resetSearch = () => {
  searchForm.processName = ''
  searchForm.summary = ''
  currentPage.value = 1
}

const handleSort = (column) => {
  if (allColumns.value.find(c => c.key === column && !c.sortable)) return
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortOrder.value = 'asc'
  }
}

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}

const goToPage = (page) => {
  if (typeof page === 'number' && page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const handleJump = () => {
  const p = parseInt(jumpPage.value)
  if (p >= 1 && p <= totalPages.value) currentPage.value = p
  else jumpPage.value = currentPage.value
}

const toggleColumnSelector = () => {
  showColumnSelector.value = !showColumnSelector.value
}

const updateVisibleColumns = () => {
  if (allColumns.value.filter(c => c.visible).length < 1) {
    const h = allColumns.value.find(c => !c.visible)
    if (h) h.visible = true
  }
}

const resetColumns = () => {
  allColumns.value.forEach(c => c.visible = true)
}

let dragSrcColumn = null
const dragStart = (e, col) => { dragSrcColumn = col; e.dataTransfer.effectAllowed = 'move' }
const dropColumn = (e, targetCol) => {
  e.preventDefault()
  if (!dragSrcColumn || dragSrcColumn.key === targetCol.key) return
  const srcIdx = allColumns.value.indexOf(dragSrcColumn)
  const tgtIdx = allColumns.value.indexOf(targetCol)
  allColumns.value.splice(srcIdx, 1)
  allColumns.value.splice(tgtIdx, 0, dragSrcColumn)
}

const draftModalVisible = ref(false)
const editingDraft = ref(null)

const editDraft = (draft) => {
  editingDraft.value = draft
  draftModalVisible.value = true
}

const handleDraftSuccess = () => {
  draftModalVisible.value = false
  editingDraft.value = null
  loadDrafts()
}

const handleDraftClose = () => {
  draftModalVisible.value = false
  editingDraft.value = null
}

const deleteDraft = async (draft) => {
  if (!await $confirm(`确认删除草稿：${draft.summary || draft.processName}？`)) return
  try {
    await deleteDraftsApi(draft.guid)
    await loadDrafts()
  } catch (e) {
    $message.error('删除失败：' + e.message)
  }
}
</script>

<style scoped>
.view-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
  overflow: hidden;
}

/* 按钮基础 */
.btn {
  padding: 6px 15px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}
.btn-primary { background-color: #1ab394; color: #fff; }
.btn-primary:hover { background-color: #18a689; }
.btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.btn-default:hover { background-color: #e7eaec; }
.btn-danger { background-color: #d9534f; color: #fff; }
.btn-danger:hover { background-color: #c9302c; }
.btn-success { background-color: #1ab394; color: #fff; }
.btn-success:hover { background-color: #18a689; }
.btn-xs { padding: 4px 8px; font-size: 12px; margin-right: 4px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }

/* 搜索区域 */
.search-panel {
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}
.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.search-item label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}
.search-item label i {
  margin-right: 2px;
  color: #1ab394;
  font-size: 12px;
}
.search-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  width: 160px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafbfc;
}
.search-input:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 3px rgba(26,179,148,0.12);
  background: #fff;
}
.search-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
  align-self: flex-end;
}
.search-btn {
  padding: 8px 18px;
  font-size: 13px;
  border-radius: 6px;
  font-weight: 500;
  border: none;
  outline: none;
  box-shadow: none;
}

/* 表格 */
.table-wrapper {
  border: 1px solid #e7eaec;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #e7eaec;
  flex-shrink: 0;
}
.table-header-left { display: flex; gap: 8px; }
.column-settings-btn { padding: 4px 10px; font-size: 12px; }

.table-container { overflow-y: auto; flex: 1; }
.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.data-table th, .data-table td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #e7eaec; white-space: nowrap; font-size: 13px; color: #676a6c; overflow: hidden; text-overflow: ellipsis; }
.data-table th { background-color: #f5f5f5; font-weight: 600; color: #676a6c; font-size: 13px; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9f9f9; }
.task-row { cursor: pointer; }
.data-table input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }

.column-header { cursor: pointer; user-select: none; white-space: nowrap; }
.column-header:hover { background-color: #e7eaec; }
.column-header i { margin-left: 4px; color: #9ca3af; font-size: 11px; vertical-align: middle; }
.sorted-asc, .sorted-desc { background-color: #ecfdf5; }

.status-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 12px; font-weight: 500; }
.status-badge.draft { background-color: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }

.action-column { white-space: nowrap; }
.action-column .btn-xs { padding: 4px 8px; font-size: 12px; border-radius: 4px; cursor: pointer; border: none; transition: all 0.2s; color: #fff; margin-right: 4px; }
.action-column .btn-primary.btn-xs { background-color: #1ab394; }
.action-column .btn-success.btn-xs { background-color: #1ab394; }
.action-column .btn-danger.btn-xs { background-color: #d9534f; }

.empty-cell { text-align: center; padding: 40px; color: #9ca3af; }

/* 分页 */
.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 13px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 10px; }
.page-numbers { display: flex; gap: 5px; }
.page-btn { width: 32px; height: 32px; border: 1px solid #e7eaec; background-color: #fff; color: #676a6c; border-radius: 4px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.page-btn:hover:not(.disabled) { background-color: #f9f9f9; border-color: #1ab394; color: #1ab394; }
.page-btn.active { background-color: #1ab394; border-color: #1ab394; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 5px; font-size: 13px; margin-right: 10px; padding-right: 10px; border-right: 1px solid #e7eaec; }
.page-size-selector span { font-size: 13px; color: #676a6c; }
.page-size-input { width: 60px; padding: 5px 8px; font-size: 13px; }
.page-jump { display: flex; align-items: center; gap: 5px; margin-left: 10px; padding-left: 10px; border-left: 1px solid #e7eaec; }
.page-jump span { font-size: 13px; color: #676a6c; }
.jump-input { width: 50px; padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; text-align: center; }
.jump-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); outline: none; }
.jump-btn { padding: 5px 12px; }

/* 列设置弹窗 */
.column-selector-modal { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.column-selector-content { background-color: #fff; border-radius: 6px; width: 350px; }
.column-selector-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-bottom: 1px solid #e7eaec; }
.column-selector-header h4 { margin: 0; font-size: 14px; }
.close-btn { background: none; border: none; cursor: pointer; color: #676a6c; font-size: 16px; }
.close-btn:hover { color: #333; }
.column-selector-body { padding: 15px; max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
.column-checkbox { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 13px; }
.column-checkbox input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
.column-selector-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
.column-selector-footer .btn { padding: 6px 15px; font-size: 13px; border-radius: 4px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; border: none; outline: none; box-shadow: none; }
.column-selector-footer .btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.column-selector-footer .btn-default:hover { background-color: #e7eaec; }
.column-selector-footer .btn-primary { background-color: #1ab394; color: #fff; }
.column-selector-footer .btn-primary:hover { background-color: #18a689; }
</style>
